import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import React from "react";

import {IntegerField} from "@khanacademy/event-schemas";
import {View} from "@khanacademy/wonder-blocks-core";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {isTruthy} from "@khanacademy/wonder-stuff-core";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import Log from "@khan/logging";
import {RequestInfo} from "@khan/shared-core";
import {mediaQueries} from "@khan/shared-styles";

import useProfile from "#data-access/use-profile.ts";
import {useIsActivityPublic} from "#guide-activities/ui/internal/use-is-activity-available.tsx";

import {
    getValidContentKinds,
    getValidTopicAncestors,
    type SearchableContentKinds,
} from "./constants.ts";
import Pagination from "./pagination.tsx";
import Result from "./result.tsx";
import StateStandardsBanner from "./state-standards-banner.tsx";
import TryThisButton from "./try-this-button.tsx";
import {useCuratedIndexedContentSearch} from "./use-curated-indexed-content-search.ts";
import {
    getContentIcon,
    getTranslatedContentType,
    isActivitySearchable,
} from "./utils.ts";

type Props = {
    /** The search query. */
    query: string;
    /** The content kinds to filter by. */
    contentKinds: SearchableContentKinds[];
    /** The allowed topic ancestors to filter by. */
    allowedTopicAncestors: string[];
    /** The default state of the google powered toggle.
     This controls how TryThisButton is rendered. */
    googlePoweredDefaultState: boolean;
    /** The function to toggle the google powered state. */
    googlePoweredToggleFunc: () => void;
    /** The page of results we will display. */
    pageNum: number;
    /** The function to set the page of results we will display. */
    setPageNum: (pageNum: number) => void;
};

const IndexedSearchResults = ({
    query,
    contentKinds,
    allowedTopicAncestors,
    googlePoweredDefaultState,
    googlePoweredToggleFunc,
    pageNum,
    setPageNum,
}: Props) => {
    const publishAnalyticsEvent = usePublishAnalyticsEvent();
    const profileResult = useProfile();

    const profile =
        profileResult.status === "success" ? profileResult.profile : null;

    const isActivityPublic = useIsActivityPublic();

    const activeFilters =
        contentKinds.length !== getValidContentKinds().length ||
        allowedTopicAncestors.length !== getValidTopicAncestors().length;

    const searchResult = useCuratedIndexedContentSearch(
        query,
        allowedTopicAncestors,
        contentKinds,
        pageNum - 1,
    );

    const handleChangePage = async (
        pageNum: number,
        direction: "previous" | "next",
    ) => {
        await publishAnalyticsEvent({
            eventType: "SearchPageNavigatedTI",
            eventSchemaVersion: 1,
            searchTerm: query,
            currentPageNumber: new IntegerField(pageNum),
            previousPageNumber: new IntegerField(
                direction === "previous" ? pageNum + 1 : pageNum - 1,
            ),
        });
        setPageNum(pageNum);
    };

    if (
        searchResult.status === "loading" ||
        searchResult.status === "no-data"
    ) {
        return (
            <View>
                <Pagination
                    pageNumber={pageNum}
                    numberOfResultsInPage={1}
                    setPageNumber={(pageNum, direction) =>
                        handleChangePage(pageNum, direction)
                    }
                />
                <CircularSpinner />
            </View>
        );
    }

    if (searchResult.status === "success") {
        const results = searchResult.data;

        if (results.length === 0) {
            return (
                <View>
                    {pageNum > 1 && (
                        <Pagination
                            pageNumber={pageNum}
                            numberOfResultsInPage={0}
                            setPageNumber={(pageNum, direction) =>
                                handleChangePage(pageNum, direction)
                            }
                        />
                    )}
                    <div>{t`No results`}.</div>
                    <div>{t`Try a different search term.`}</div>
                    {activeFilters && (
                        <div>{t`Try clearing your filters.`}</div>
                    )}
                    <TryThisButton
                        locale={RequestInfo.kaLocale}
                        isDefaultState={googlePoweredDefaultState}
                        toggleFunc={googlePoweredToggleFunc}
                        tryNotSee={true}
                    />
                </View>
            );
        }

        const resultsList = results
            .map(({item, path}, index) => {
                // NOTE(michaelpolyak): Due to historic reasons most of the
                // content schema is optional, but in practice these fields
                // are always set in publish.
                const {
                    id: contentId,
                    contentKind,
                    slug,
                    translatedTitle,
                    translatedDescription,
                } = item;

                if (item.__typename === "AIGuideActivity") {
                    if (
                        slug &&
                        !(isActivityPublic(slug) && isActivitySearchable(slug))
                    ) {
                        return null;
                    }

                    return (
                        <li key={`AIGuideActivity:${contentId}`}>
                            <Result
                                index={index}
                                headerLeft={getTranslatedContentType(
                                    contentKind,
                                )}
                                headerRight={t`Khanmigo`}
                                title={translatedTitle}
                                description={translatedDescription}
                                url={item.defaultUrlPath}
                                icon=""
                                iconUrl="/images/guide/khanmigo-blushing.svg"
                                contentKind={contentKind}
                                contentId={contentId}
                                currentPage={pageNum}
                            />
                        </li>
                    );
                }

                if (item.__typename === "Topic") {
                    return (
                        <li key={`${contentKind}:${contentId}`}>
                            <Result
                                index={index}
                                headerLeft={getTranslatedContentType(
                                    contentKind,
                                )}
                                headerRight={path.courseTitle}
                                title={translatedTitle}
                                description={translatedDescription}
                                url={item.relativeUrl}
                                icon=""
                                iconUrl={
                                    item.iconPath ||
                                    path.courseIconPath ||
                                    path.domainIconPath
                                }
                                contentKind={contentKind}
                                contentId={contentId}
                                currentPage={pageNum}
                            />
                        </li>
                    );
                }

                return (
                    <li key={`${contentKind}:${contentId}`}>
                        <Result
                            index={index}
                            headerLeft={getTranslatedContentType(contentKind)}
                            headerRight={path.courseTitle}
                            title={translatedTitle}
                            description={translatedDescription}
                            url={path.contentUrl}
                            icon={getContentIcon(contentKind)}
                            iconUrl=""
                            contentKind={contentKind}
                            contentId={contentId}
                            currentPage={pageNum}
                        />
                    </li>
                );
            })
            .filter(isTruthy);

        const PaginationWrappedResultsList = (
            <View>
                {/* eslint-disable-next-line jsx-a11y/heading-has-content -- TODO: Address a11y error */}
                <h2 aria-label={t`Search results`} />
                <Pagination
                    pageNumber={pageNum}
                    numberOfResultsInPage={results.length}
                    setPageNumber={(pageNum, direction) => {
                        handleChangePage(pageNum, direction);
                    }}
                />
                <ul>{resultsList}</ul>
                <Pagination
                    pageNumber={pageNum}
                    numberOfResultsInPage={results.length}
                    setPageNumber={(pageNum, direction) => {
                        handleChangePage(pageNum, direction);
                    }}
                />
            </View>
        );

        const shouldSeeBanner =
            pageNum === 1 &&
            RequestInfo.kaLocale === "en" &&
            // In some (all?!) cases, logged-out users don't have a profile.
            (!profile ||
                profile?.get("isPhantom") ||
                profile?.get("isTeacher") ||
                profile?.get("isParent"));

        Log.log(
            "Should see state standards banner",
            null,
            shouldSeeBanner,
            pageNum,
            profile?.get("isPhantom"),
            profile?.get("isTeacher"),
            profile?.get("isParent"),
        );

        return (
            <View
                id="indexed-search-results"
                style={styles.searchResultsContainer}
            >
                {shouldSeeBanner && <StateStandardsBanner />}
                {results && PaginationWrappedResultsList}
            </View>
        );
    }

    return <View>{t`There was an error retrieving the search results`}.</View>;
};

const styles = StyleSheet.create({
    searchResultsContainer: {
        [mediaQueries.smOrSmaller]: {
            paddingLeft: 0,
            paddingRight: 0,
        },
    },
});

export default IndexedSearchResults;
